import { computed, watch } from 'vue';
import { useInfiniteQuery } from '@tanstack/vue-query';
import { useApi, useToaster } from '@shopexperts/shared';
import type { ExpertReviewRequestsQueryParams, ReviewRequestSchema } from '@shopexperts/shared';
import { z } from 'zod';

type ReviewRequest = z.infer<typeof ReviewRequestSchema>;

export function useExpertReviewRequests(status?: ExpertReviewRequestsQueryParams['status']) {
  const api = useApi();
  const toaster = useToaster();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: ['expert', 'review-requests', status],
    queryFn: ({ pageParam }) => {
      return api.expert.reviewsV2.getReviewRequests({
        query: { limit: 20, cursor: pageParam, status },
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined;
    },
    initialPageParam: undefined as string | undefined,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  watch(error, (newError) => {
    if (newError) {
      toaster.create({
        title: 'Failed to load review requests',
        description: newError.message || 'An error occurred while fetching review requests.',
        type: 'error',
      });
      console.error('[useExpertReviewRequests] Error:', newError);
    }
  });

  const reviewRequests = computed<ReviewRequest[]>(() => {
    return data.value?.pages.flatMap((page) => page.data) ?? [];
  });

  return {
    reviewRequests,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    error,
  };
}

